import { defineComponent as _defineComponent } from 'vue'
import {computed, ref} from "vue";
import {BarChart} from "echarts/charts";
import { use } from "echarts/core";
import {SVGRenderer} from "echarts/renderers";
import {LegendComponent, TitleComponent, TooltipComponent, GridComponent} from "echarts/components";
import {useRouter} from "vue-router";
import {Proposal, ProposalStatus } from "@/models/store/proposal";
import { createProposalListChartData } from '@/charts/governance';
import { useProposalsStore } from '@/store/proposals.store';
import CoinAmount from '../commons/CoinAmount.vue';
import PercentsView from "@/components/commons/PercentsView";
import DateCommon from "@/components/commons/DateCommon.vue";
import {BigIntWrapper} from "@/models/store/common";
import {BigDecimal} from "@/models/store/big.decimal";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProposalGovernance',
  props: {
    proposal: { type: Object, required: true }
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

use([
  SVGRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
]);

const props = __props;

const router = useRouter();

const icons  = new Map<string, string>([
  [ProposalStatus.PASSED, "CheckSquare"],
  [ProposalStatus.REJECTED, "XCircle"],
  [ProposalStatus.DEPOSIT_PERIOD, ""],
  [ProposalStatus.FAILED, ""],
  [ProposalStatus.VOTING_PERIOD, ""],
  [ProposalStatus.UNSPECIFIED, ""]
]);

const tooltipOption = ref('');
const tooltipValue = ref('');
const showChartTooltip = ref(false);
const tooltipPosX = ref(0);
const tooltipPosY = ref(0);
const tooltipBorderColor = ref('');
const proposalStore = useProposalsStore();

const showTooltip = (option, value) => {
  if(option == 'YES') {
    tooltipBorderColor.value = '#72bf44';
  }
  if(option == 'ABSTAIN') {
    tooltipBorderColor.value = '#27697f';
  }
  if(option == 'NO') {
    tooltipBorderColor.value = '#861010';
  }
  if(option == 'NO_WITH_VETO') {
    tooltipBorderColor.value = '#FDDB2A';
  }
  if(option == 'THRESHOLD') {
    tooltipBorderColor.value = '#000000';
  }
  if(option == 'QUORUM') {
    tooltipBorderColor.value = '#000000';
  }
  if(option == 'NOT_VOTED') {
    tooltipBorderColor.value = '#8d8d8d';
  }
  tooltipOption.value = option;
  tooltipValue.value = value;
  showChartTooltip.value = true;
};

const updateTooltipPosition = (e) => {
  let x = e.clientX;
  let y = e.clientY;

  tooltipPosX.value = x;
  tooltipPosY.value = y - 80;
};

function hideTooltip(){
    showChartTooltip.value = false;
}
const thresholdPercentage = computed(() => {
  return useProposalsStore().getTallyParams.threshold * 100;
});

const thresholdPercentagePosition = computed(() => {
  return yesPercentage.value.add(noPercentage.value).subtract(abstainPercentage.value).subtract(noWithVetoPercentage.value).multiply(useProposalsStore().getTallyParams.threshold * 100)
});

const quorumPercentage = computed(() => {
  return useProposalsStore().getTallyParams.quorum * 100;
});

const yesPercentage = computed(() => {
  const yesPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getYesPercentage();
  return yesPercentage!=undefined ? yesPercentage : new BigDecimal(0);
});

const noPercentage = computed(() => {
  const noPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getNoPercentage();
  return noPercentage!=undefined ? noPercentage : new BigDecimal(0);
});

const abstainPercentage = computed(() => {
  const abstainPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getAbstainPercentage();
  return abstainPercentage != undefined ? abstainPercentage : new BigDecimal(0);
});

const noWithVetoPercentage = computed(() => {
  const noWithVetoPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getNoWithVetoPercentage();
  return noWithVetoPercentage != undefined ? noWithVetoPercentage : new BigDecimal(0);
});

// const notVotedPercentage = computed(() => {
//   const notVotedPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getNotVotedPercentage();
//   return notVotedPercentage != undefined ? notVotedPercentage : new BigDecimal(0);
// });

const yesPercentageChart = computed(() => {
  const yesPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getYesPercentageChart();
  return yesPercentage!=undefined ? yesPercentage : new BigDecimal(0);
});

const noPercentageChart = computed(() => {
  const noPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getNoPercentageChart();
  return noPercentage!=undefined ? noPercentage : new BigDecimal(0);
});

const abstainPercentageChart = computed(() => {
  const abstainPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getAbstainPercentageChart();
  return abstainPercentage != undefined ? abstainPercentage : new BigDecimal(0);
});

const noWithVetoPercentageChart = computed(() => {
  const noWithVetoPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getNoWithVetoPercentageChart();
  return noWithVetoPercentage != undefined ? noWithVetoPercentage : new BigDecimal(0);
});

const notVotedPercentageChart = computed(() => {
  const notVotedPercentage = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getNotVotedPercentageChart();
  return notVotedPercentage != undefined ? notVotedPercentage : new BigDecimal(0);
});

const yes = computed(() => {
  const yes = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getYes();
  return yes != undefined ? yes : 0n;
});

const no = computed(() => {
  const no = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getNo();
  return no!=undefined ? no : 0n;
});

const abstain = computed(() => {
  const abstain = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getAbstain();
  return abstain != undefined ? abstain : 0n;
});

const noWithVeto = computed(() => {
 const noWithVeto = proposalStore.getProposalDetailsTallyById(props.proposal.proposalId)?.getNoWithVeto();
 return noWithVeto!=undefined ? noWithVeto : 0n;
});

const sumOfVotes = computed(() => {
  const val = useProposalsStore().getProposalTally(props.proposal).total;
  return val > 0n ? val : -1n;
});


const showDetailsClick = () => {
  router.push({name: 'governanceDetails', params: {id: props.proposal.proposalId}});
};

const option = computed(() => {
  return createProposalListChartData(
    {
      amount: yes.value,
      percentage: yesPercentage.value
    },
    {
      amount: abstain.value,
      percentage: abstainPercentage.value
    },
    {
      amount: no.value,
      percentage: noPercentage.value
    },
    {
      amount: noWithVeto.value,
      percentage: noWithVetoPercentage.value
    },
    sumOfVotes.value
  );
});

const getTitle = () => {
  if(props.proposal?.content?.title) {
    return props.proposal?.content?.title;
  } else if(props.proposal && props.proposal?.metaData) {
    try {
      return JSON.parse(props.proposal.metaData).description;
    } catch (e) {
      console.log(e);
    }
  }
};

const __returned__ = { props, router, icons, tooltipOption, tooltipValue, showChartTooltip, tooltipPosX, tooltipPosY, tooltipBorderColor, proposalStore, showTooltip, updateTooltipPosition, hideTooltip, thresholdPercentage, thresholdPercentagePosition, quorumPercentage, yesPercentage, noPercentage, abstainPercentage, noWithVetoPercentage, yesPercentageChart, noPercentageChart, abstainPercentageChart, noWithVetoPercentageChart, notVotedPercentageChart, yes, no, abstain, noWithVeto, sumOfVotes, showDetailsClick, option, getTitle, get ProposalStatus() { return ProposalStatus }, CoinAmount, get PercentsView() { return PercentsView }, DateCommon, get BigIntWrapper() { return BigIntWrapper } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})